import utils from "./Utils.js";
import plotMgrInstance from "./PlotMgr.js"
import * as d3 from "d3";
import {Plot} from "./Plot.js"

class LinePlot extends Plot {
    constructor(initParams) {
        super(initParams);
    }

    addLinePlot(newData, elementId, lineKeys, sortingKey, flipAxis = true, chartFormatting, showLineLables) {
        try {
            let scope = this;

            let formatting = { ...utils.defaultFormatting, ...(chartFormatting || {}) };
            scope.formatting = formatting
            let dataObj = {};
            let dataArr = [];

            scope.canvas.innerHTML = '';
            // Create the donut plot layout for number or count layout type
            let response = utils.filterLineData(newData, elementId, lineKeys, sortingKey);
            if (response.status == 'success') {
                dataObj = response.data;
            }
            dataArr = dataObj.data;

            dataArr.forEach(element => {
                scope.data[element.ids] = element;
                scope.groups[element.ids] = [];
            });

            scope.dataObj = dataObj
            scope.dataArr = dataArr

            const sumstat = d3.group(dataArr, d => d.key);
            
            //Calc amount of padding on right needed to prevent labels getting cut off
            scope.paddingRight = 0
            if (showLineLables){
                let lineKeysCharLength = utils.getMaxCharactersForString(lineKeys)
                scope.paddingRight = lineKeysCharLength*5
            }

            //Calc padding needed on left and bottom to prevent axis text from being cut off
            let axisAngleRadians = utils.toRadians(formatting.axisAngle)
            let values = [dataObj.settings.min, dataObj.settings.max]
            let maxChars = utils.getMaxCharactersForNumber(values)
            let paddingLeft = maxChars * Math.abs(Math.cos(axisAngleRadians)) * 5
            scope.paddingLeft = paddingLeft
            
            let numDataPoints = dataArr.length
            let numChars = utils.getMaxCharactersForNumber([numDataPoints])
            let paddingBottom = numChars * Math.abs(Math.sin(axisAngleRadians)) * 5
            scope.paddingBottom = paddingBottom
            
            // set the dimensions and margins of the graph
            let width = scope.canvas.clientWidth - scope.margin.left - scope.paddingRight - scope.margin.right -scope.paddingLeft,
                height = scope.canvas.clientHeight - scope.margin.top - scope.margin.bottom - paddingBottom;

            let dimensions = "0 0 " + scope.canvas.offsetWidth + " " + scope.canvas.offsetHeight;

            let items = {};
            let lines = {};
            let axisX = {};
            let axisY = {};
            let gridX = {};
            let gridY = {};

            // append the svg object to the body of the page
            scope.svg = d3.select(scope.canvas)
                .append('svg')
                .attr("viewBox", dimensions)
                .attr("preserveAspectRatio", "xMinYMin meet")
                .append('g')
                .attr('transform', 'translate(' + scope.margin.left + ',' + scope.margin.top + ')');

                scope.tooltip = d3.select(scope.canvas).append("div");
            utils.formatTooltipDefault(scope.tooltip, scope.defaultColor);

            // █▀▀ █▀▀ ▄▀▄ █   █▀▀
            // ▄██ █▄▄ █▀█ █▄▄ ██▄ 

            // Add X axis --> it is a date format
            const x = d3.scaleLinear()
                .domain([dataObj.settings.total, 0])
                .range([width, 0]);

            // Add Y axis
            const y = d3.scaleLinear()
                .domain([dataObj.settings.min, dataObj.settings.max])
                .range([height, 0]);

            // ▄▀▄ ▀▄▀ ▀█▀ █▀▀ 
            // █▀█ █ █ ▄█▄ ▄██ 

            axisX = scope.svg.append("g")
                .attr('name', 'axisX')
                .attr("transform", `translate(${scope.paddingLeft}, ${height})`)
                .call(d3.axisBottom(x))

            axisY = scope.svg.append("g")
                .attr('name', 'axisY')
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .call(d3.axisLeft(y))

            // █▀▀ █▀█ ▀█▀ █▀▄ 
            // █▄█ █▀▄ ▄█▄ █▄▀ 

            gridX = scope.svg.append("g")
                .attr('name', 'gridX')
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .call(d3.axisLeft(y).tickSize(-width).tickFormat(""));

            gridY = scope.svg.append("g")
                .attr('name', 'gridY')
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .call(d3.axisBottom(x).tickSize(height).tickFormat(""));

            // ▀█▀ ▀█▀ █▀▀ █▄ ▄█ █▀▀ 
            // ▄█▄  █  ██▄ █ ▀ █ ▄██ 

            lines = scope.svg.selectAll(".line")
                .data(sumstat)
                .join("path")
                .attr('fill', 'none')
                .attr("class", function (d) { return "line " + d.id })
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .attr('stroke-width', scope.defaultWidth)
                .attr("d", function (d) {
                    return d3.line()
                        .x(function (d) { return x(+d.x); })
                        .y(function (d) { return y(+d.y); })
                        (d[1])
                })
                .style('pointer-events', 'none') // restricted mouse events
                .style('user-select', 'none') // stanard
                .style('-webkit-user-select', 'none') // safari
                .style('-ms-user-select', 'none'); // IE10 and IE11);
            

            // Add the dots
            items = scope.svg
                .selectAll("dot")
                .data(dataArr)
                .join("circle")
                .attr('visibility', d => d.value ? 'visible' : 'hidden')
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .attr("cx", d => x(d.x))
                .attr("cy", d => y(d.y))
                .attr("r", scope.defaultRadius)
                .attr("data-id", function (d, i) {
                    scope.groups[d.ids].push(this);
                    return d.ids;
                })

            //Add Plot Labels
            if (showLineLables){
                scope.svg
                .selectAll(".text")
                .data(dataObj.keys)
                .join("text")
                .attr("x", d => x(d.x)+5)
                .attr("y", d => y(d.y))
                .attr("class", function (d) { return "label " + d.key })
                .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
                .attr("font-size", "0.65em")
                .style("fill", scope.defaultColor)
                .text(d => d.key)
                .style('pointer-events', 'none') // restricted mouse events
                .style('user-select', 'none') // stanard
                .style('-webkit-user-select', 'none') // safari
                .style('-ms-user-select', 'none'); // IE10 and IE11)
            }
            

            // █▀▀ █▀█ █▀█ █▄ ▄█ ▄▀▄ ▀█▀ ▀█▀ ▀█▀ █▄ █ █▀▀ 
            // █▀  █▄█ █▀▄ █ ▀ █ █▀█  █   █  ▄█▄ █ ▀█ █▄█ 

            utils.formatStrokeItem(lines, scope.tooltip, scope.defaultColor, 0.8);
            utils.formatFillItem(items, scope.tooltip, scope.defaultColor, scope.defaultOpacity);
            utils.formatAxis(axisX, formatting.xAxis, scope.axisColor,1.0, formatting.axisAngle);
            utils.formatAxis(axisY, formatting.yAxis, scope.axisColor,1.0,formatting.axisAngle);
            utils.formatGrid(gridX, formatting.xGrid, scope.gridColor, scope.gridOpacity);
            utils.formatGrid(gridY, formatting.yGrid, scope.gridColor, scope.gridOpacity);

            let returnValue = {
                'status': 'success',
                'message': 'Line plot added'
            }
            scope.plotAdded = true
            return returnValue;
        }
        catch (err) {
            let returnValue = {
                'status': 'failed',
                'message': 'Failed to add Line plot.' + err.message
            }
            return returnValue;
        }
    }

    resizeChart(){
        let scope = this;

        // Redefine dimensions based on the updated canvas size
        let width = scope.canvas.clientWidth - scope.margin.left - scope.paddingRight - scope.margin.right - scope.paddingLeft;
        let height = scope.canvas.clientHeight - scope.margin.top - scope.margin.bottom - scope.paddingBottom - 15;

        // Append SVG object to the canvas
        d3.select(scope.canvas).select("svg")
            .attr("viewBox", `0 0 ${scope.canvas.offsetWidth} ${scope.canvas.offsetHeight}`)

        // Recompute the scales
        const x = d3.scaleLinear()
            .domain([scope.dataObj.settings.total, 0])
            .range([width, 0]);

        const y = d3.scaleLinear()
            .domain([scope.dataObj.settings.min, scope.dataObj.settings.max])
            .range([height, 0]);

        // Redefine axes and grid lines
        let axisX = scope.svg.selectAll('g[name="axisX"]')
            .attr("transform", `translate(${scope.paddingLeft}, ${height})`)
            .call(d3.axisBottom(x));

        let axisY = scope.svg.selectAll('g[name="axisY"]')
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
            .call(d3.axisLeft(y));

        let gridX = scope.svg.selectAll('g[name="gridX"]')
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
            .call(d3.axisLeft(y).tickSize(-width).tickFormat(""));

        let gridY = scope.svg.selectAll('g[name="gridY"]')
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)
            .call(d3.axisBottom(x).tickSize(height).tickFormat(""));
        
        // Update the lines with the new data
        let lines = scope.svg.selectAll("path.line")
            .attr("d", function (d) {
                return d3.line()
                    .x(function (d) { return x(+d.x); })
                    .y(function (d) { return y(+d.y); })
                    (d[1])
            })
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)

        // Redraw dots
        let items = scope.svg.selectAll("circle")
            .attr("cx", d => x(d.x))
            .attr("cy", d => y(d.y))
            .attr("r", scope.defaultRadius)
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)

        // Redraw labels
        scope.svg.selectAll("text.label")
            .attr("x", d => x(d.x) + 5)
            .attr("y", d => y(d.y))
            .attr("transform", `translate(${scope.paddingLeft}, ${0})`)

        // Reapply formatting
        utils.formatStrokeItem(lines, scope.tooltip, scope.defaultColor, 0.8);
        utils.formatFillItem(items, scope.tooltip, scope.defaultColor, scope.defaultOpacity);
        utils.formatAxis(axisX, scope.formatting.xAxis, scope.axisColor, 1.0, scope.formatting.axisAngle);
        utils.formatAxis(axisY, scope.formatting.yAxis, scope.axisColor, 1.0, scope.formatting.axisAngle);
        utils.formatGrid(gridX, scope.formatting.xGrid, scope.gridColor, scope.gridOpacity);
        utils.formatGrid(gridY, scope.formatting.yGrid, scope.gridColor, scope.gridOpacity);
        
        scope.colorElements()
    }


    //  ████  ███  █      ███  ████     ████  █   █ 
    // █     █   █ █     █   █ █   █    █   █  █ █  
    // █     █   █ █     █   █ ████     ████    █   
    // █     █   █ █     █   █ █   █    █   █   █   
    //  ████  ███  █████  ███  █   █    ████    █   


    colorElements(ids, colors) {
        let scope = this;

        if (!ids || !colors){
            if (!scope.colorBy) return
            ids = scope.colorBy.ids
            colors = scope.colorBy.colors
        }
        else{
            scope.colorBy = {
                ids: ids,
                colors: colors
            }
        }

        Object.keys(scope.groups).forEach(key => {
            let index = ids.indexOf(key);
            scope.groups[key].forEach(element => {
                let highlightedElement = d3.select(element);
                scope.applyColor(highlightedElement, colors[index])
            });
        });
    }


    // █████ █████ █     █████  ████ █████ 
    // █     █     █     █     █       █   
    // █████ ████  █     ████  █       █   
    //     █ █     █     █     █       █   
    // █████ █████ █████ █████  ████   █   


    // SELECTION event
    getSelectedElementsCondition(group, selectedElement, callBack, ids){
        let scope = this;

        if (scope.data[selectedElement.attr('data-id')]) {
            let id = scope.data[selectedElement.attr('data-id')].ids;
            scope.selectedElements.push(id)

            scope.groups[id].forEach(element => {
                scope.applySelectionColor(d3.select(element));
            });
            callBack([...new Set(scope.selectedElements)]);
        }
        else {
            callBack('');
            plotMgrInstance.deSelectElementsInAllPlots();
        }
    }


    selectElements(elementIDs) {
        let scope = this;

        if (scope.svg != undefined || scope.svg != null) {
            if (elementIDs) {
                elementIDs.forEach(key => {
                    if(scope.groups[key]){
                        scope.groups[key].forEach(element => {
                        scope.applySelectionColor(d3.select(element));
                    });
                }
                });
            }
        }
    }


    // █   █ █████  ████ █   █ █     █████  ████ █   █ █████ 
    // █   █   █   █     █   █ █       █   █     █   █   █   
    // █████   █   █  ██ █████ █       █   █  ██ █████   █   
    // █   █   █   █   █ █   █ █       █   █   █ █   █   █   
    // █   █ █████  ███  █   █ █████ █████  ███  █   █   █   

    // Highlight event
    highlightGroup(highlightedElement, event, callBack,){
        let scope = this;
        let id = highlightedElement.attr('data-id');
        if (id != undefined) {
            scope.groups[id].forEach(element => {
                scope.applyHighlightColor(d3.select(element));
            });
            scope.applyToolTip(event, id);
            callBack([scope.data[id].ids]);
        }
        else {
            callBack("");
            plotMgrInstance.deHighlightElementsInAllPlots();
        }
    }

    highlightElements(elementIDs) {
        let scope = this;

        if (scope.highlightElementFlagCheck) {
            if (scope.svg != undefined) {
                if (elementIDs) {
                    elementIDs.forEach(key => {
                        if(scope.groups[key]){
                            scope.groups[key].forEach(element => {
                            scope.applyHighlightColor(d3.select(element));
                        });
                    }
                    });
                }
            }
        }
    }

   //Apply ToolTip
   buildTooltipContent(id) {
    let scope = this;
    let content = "<strong> Index </strong> | " + utils.setDigits(scope.data[id].x, scope.digits) + "<br/>";
    content += "<strong>" + scope.data[id].key + "</strong> | " + utils.setDigits(scope.data[id].y, scope.digits) + "<br/>";
    return content;
  }

}
export { LinePlot }
